import { useCallback, useRef } from 'react';

/** Fastest a person could plausibly fill a short form. Anything quicker is scripted. */
const MIN_FILL_TIME_MS = 3000;

/** Nobody legitimately sends the same form twice within five minutes. */
const SUBMISSION_COOLDOWN_MS = 5 * 60 * 1000;

/** Form spam almost always carries a link. No real name, job title or company does. */
const LINK_PATTERN = /https?:\/\/|www\.|\[url/i;

type UseBotCheckArgs = {
    /** Per-form localStorage key for the cooldown. Two forms must never share one. */
    storageKey: string;
};

type BotCheckArgs = {
    /** Value of the honeypot input. It is invisible to people, so anything here is a bot. */
    honeypot?: string;
    /** Free-text values to scan for injected links. */
    textFields: (string | undefined)[];
};

function readLastSubmittedAt(storageKey: string): number | null {
    try {
        const stored = window.localStorage.getItem(storageKey);
        if (stored === null) {
            return null;
        }

        const parsed = Number(stored);
        return Number.isFinite(parsed) ? parsed : null;
    } catch {
        // Storage can be unavailable (private mode, blocked cookies). Fail open so
        // that a real visitor is never turned away by a check we cannot run.
        return null;
    }
}

/**
 * Invisible bot heuristics for public forms. Every signal is tuned so a real
 * visitor cannot trip it, and the caller is expected to fail silently when one
 * fires — telling a bot it was caught only teaches its operator to adapt.
 */
function useBotCheck({ storageKey }: UseBotCheckArgs) {
    const mountedAtRef = useRef(Date.now());
    const hasInteractedRef = useRef(false);

    const registerInteraction = useCallback(() => {
        hasInteractedRef.current = true;
    }, []);

    /**
     * Spread onto the form element. A script that assigns `input.value` directly
     * fires none of these, while a person cannot fill the form without firing at
     * least one — including when pasting, using browser autofill, or reaching the
     * fields through assistive technology rather than a mouse or keyboard.
     */
    const interactionProps = {
        onPointerDown: registerInteraction,
        onKeyDown: registerInteraction,
        onPaste: registerInteraction,
        onFocus: registerInteraction,
    };

    const isBotSubmission = useCallback(({ honeypot, textFields }: BotCheckArgs) => {
        if (honeypot) {
            return true;
        }

        if (!hasInteractedRef.current) {
            return true;
        }

        if (Date.now() - mountedAtRef.current < MIN_FILL_TIME_MS) {
            return true;
        }

        if (textFields.some((value) => value !== undefined && LINK_PATTERN.test(value))) {
            return true;
        }

        const lastSubmittedAt = readLastSubmittedAt(storageKey);
        return lastSubmittedAt !== null && Date.now() - lastSubmittedAt < SUBMISSION_COOLDOWN_MS;
    }, [storageKey]);

    const recordSubmission = useCallback(() => {
        try {
            window.localStorage.setItem(storageKey, String(Date.now()));
        } catch {
            // See readLastSubmittedAt: an unavailable store only costs us the cooldown.
        }
    }, [storageKey]);

    return { interactionProps, isBotSubmission, recordSubmission };
}

export default useBotCheck;
